import { useState } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, TextToolState } from "@impero-com/ui";

import {
    EntityOverviewDtoContent,
    UpdatableEntitiesAccessRights,
} from "api/generated/model";
import { useUpdateEntitiesAccessRightsMutation } from "org/entities/queries/useUpdateEntitiesAccessRightsMutation";

import { AccessRightChanges, editableScopes } from "./accessRightChanges";
import { successMessage } from "./successMessage";

interface UseAssignUsersUtilsProps {
    selected: EntityOverviewDtoContent[];
    deselectAll: () => void;
}

export function useAssignUsersUtils({
    selected,
    deselectAll,
}: UseAssignUsersUtilsProps) {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    // The modal works on the selection it was opened with, so it stays stable while the list
    // refetches and while it animates out after the selection is cleared.
    const [modalSelection, setModalSelection] = useState<
        EntityOverviewDtoContent[]
    >([]);
    const { mutate, isPending } = useUpdateEntitiesAccessRightsMutation();

    const assignUsers = (
        update: UpdatableEntitiesAccessRights,
        changes: AccessRightChanges
    ) => {
        mutate(
            { data: update },
            {
                onSuccess: () => {
                    emitNotification(
                        "success",
                        successMessage(t, update.change, changes)
                    );
                    deselectAll();
                    setIsModalOpen(false);
                },
            }
        );
    };

    return {
        assignUsersToolState: toolState(
            t,
            selected.length,
            editableScopes(selected.map(({ actions }) => actions)).length
        ),
        modalEntities: modalSelection.map(({ resource }) => resource),
        modalScopes: editableScopes(
            modalSelection.map(({ actions }) => actions)
        ),
        isAssignUsersModalOpen: isModalOpen,
        openAssignUsersModal: () => {
            setModalSelection(selected);
            setIsModalOpen(true);
        },
        closeAssignUsersModal: () => setIsModalOpen(false),
        assignUsers,
        isAssigningUsers: isPending,
    };
}

function toolState(
    t: TFunction,
    selectedCount: number,
    editableScopeCount: number
): TextToolState {
    if (selectedCount === 0) {
        return { type: "DISABLED" };
    }
    if (editableScopeCount === 0) {
        return {
            type: "DISABLED",
            reason: t("ControlEntities_ManageAccessRights_NoRightsReason"),
        };
    }
    return { type: "ENABLED" };
}
